/**
 * Solar API Service
 * RoofMapper v3 - Google Solar API Integration
 */

import { apiClient } from './api';
import { LatLng, SolarAPIResponse } from '../types/solar';

export const solarService = {
  /**
   * Fetch building insights and data layers from backend
   * Backend proxies the Google Solar API request
   */
  async getBuildingInsights(
    position: LatLng,
    options?: {
      cacheRadius?: number;  // Cache radius in meters (default: 100)
      force?: boolean;        // Force refresh, bypass cache
    }
  ): Promise<SolarAPIResponse> {
    // Add cache-busting headers when force is enabled
    const headers = options?.force ? {
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0',
    } : {};

    const { data } = await apiClient.post<SolarAPIResponse>(
      '/api/solar/building-insights',
      {
        lat: position.lat,
        lng: position.lng,
        cache_radius: options?.cacheRadius,
        force: options?.force,
      },
      { headers }
    );
    return data;
  },

  /**
   * Save measurement to MongoDB
   */
  async saveMeasurement(payload: {
    rpu: string;
    measurementType: 'solar' | 'manual';
    roofArea: number;
    panelCount: number;
    systemSize: number;
    panelWatts: number;
    panelModel?: string;
    coordinates?: LatLng[];
    efficiency?: number;
    estimatedGeneration?: number;
  }): Promise<void> {
    await apiClient.post('/api/save-measurement', payload);
  },

  /**
   * Save calibration offset
   */
  async saveCalibration(payload: {
    rpu: string;
    x_offset_meters: number;
    y_offset_meters: number;
  }): Promise<void> {
    await apiClient.post('/api/save-calibration', payload);
  },

  /**
   * Perform manual measurement
   */
  async measureManual(payload: {
    lat: number;
    lng: number;
    points: LatLng[];
    panel_watts: number;
    panel_size?: number;
  }): Promise<{
    area: number;
    panelCount: number;
    systemSize: number;
  }> {
    const { data } = await apiClient.post('/api/measure-manual', payload);
    return data;
  },
};

export default solarService;
